import React, { useState, useEffect } from "react";
import { get } from "../../services/api";
import { VoucherContent } from "../NewAdmissionModule/MonthlyFeeVoucher"; // Import from custom voucher

function BulkFeeVoucher() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  // ---------- State ----------
  const [centersData, setCentersData] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [sessionPeriods, setSessionPeriods] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);

  const [selectedCenterId, setSelectedCenterId] = useState(savedCenterId || "");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");

  const [vouchers, setVouchers] = useState([]);
  const [showVouchers, setShowVouchers] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch Centers
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCentersData(response || []);
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };
    if (organizationId && token) fetchCenters();
  }, [organizationId, token]);

  // Fetch Sessions for the Center
  useEffect(() => {
    const fetchSessions = async () => {
      if (!selectedCenterId) {
        setSessionsData([]);
        return;
      }
      try {
        const response = await get(
          `FeePlan/GetCenterSessionsByCenterId?centerId=${selectedCenterId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessionsData(response.centerSessions || []);
        if (response.centerSessions && response.centerSessions.length > 0) {
          setSelectedSessionId(response.centerSessions[0].id);
        }
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };
    fetchSessions();
  }, [selectedCenterId, token]);

  // Fetch Session Periods (Months)
  useEffect(() => {
    const fetchSessionPeriods = async () => {
      if (!selectedSessionId) {
        setSessionPeriods([]);
        return;
      }
      try {
        const response = await get(
          `Session/GetSessionPeriodBySessionId?sessionId=${selectedSessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessionPeriods(response.session || []);
      } catch (error) {
        console.error("Error fetching session periods:", error);
      }
    };
    fetchSessionPeriods();
  }, [selectedSessionId, token]);

  // Fetch Classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!selectedCenterId) {
        setClassesData([]);
        return;
      }
      try {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${selectedCenterId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassesData(response || []);
      } catch (error) {
        console.error("Error fetching classes:", error);
      }
    };
    fetchClasses();
  }, [selectedCenterId, token]);

  // Fetch Sections
  useEffect(() => {
    const fetchSections = async () => {
      if (!selectedClassId || !selectedCenterId) {
        setSectionsData([]);
        return;
      }
      try {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${selectedClassId}&centerId=${selectedCenterId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSectionsData(response || []);
      } catch (error) {
        console.error("Error fetching sections:", error);
      }
    };
    fetchSections();
  }, [selectedClassId, selectedCenterId, token]);

  // Handle Apply button
  const handleApply = async () => {
    if (!selectedClassId || !selectedPeriodId) {
      alert("Please select Class and Session Period (Month).");
      return;
    }
    setLoading(true);
    try {
      const response = await get(
        `StudentChallan/GetClassSectionFeeReport?CenterId=${selectedCenterId}&ClassId=${selectedClassId || 0}&SectionId=${selectedSectionId || 0}&InstallmentId=${selectedPeriodId || 0}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response && response.result) {
        // Group by challanNo (or studentName if challanNo is missing)
        const grouped = response.result.reduce((acc, current) => {
          // Robust grouping key using 'code' (A/C #) as it's unique per student.
          // Fallback to studentName + fatherName if code is missing.
          const key = current.code 
            ? current.code 
            : `${current.studentName || 'NoName'}_${current.fatherName || 'NoFather'}`;
            
          if (!acc[key]) {
            acc[key] = {
              ...current,
              fees: [],
              totalAmount: 0,
            };
          }
          acc[key].fees.push({
            feeTypeName: current.feeTypeName,
            amount: current.amount,
            feeTypeId: current.feeTypeId
          });
          acc[key].totalAmount += current.amount;
          return acc;
        }, {});

        // Map to flat array of unique vouchers
        const uniqueVouchers = Object.values(grouped).map(group => {
            // Helper to find specific fee amount by name/id
            const getFee = (namePrefix) => {
                const fee = group.fees.find(f => f.feeTypeName?.toLowerCase().includes(namePrefix.toLowerCase()));
                return fee ? fee.amount : 0;
            };

            // Formatting date based on API output, default 'N/A'
            const formatDate = (dateString) => {
                if (!dateString) return "N/A";
                const date = new Date(dateString);
                return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            };

            return {
                id: group.code || group.challanNo || Math.random(), // unique key for React rendering list
                centerId: group.centerId,
                
                // Fields for default voucher
                studentName: group.studentName || "N/A",
                rollNo: group.code || "N/A",  // The user said passing 'code' to A/C #
                className: group.className || "N/A",
                section: group.sectionName || "N/A",
                month: group.challanMonth || sessionPeriods.find(p => p.id === Number(selectedPeriodId))?.name || "",
                feeAmount: group.totalAmount, // sum of all fees
                dueDate: formatDate(group.dueDate),
                voucherNo: group.challanNo || "N/A",
                
                // Fields specifically for Center 2076 / custom MonthlyFeeVoucher style
                centerImagePath: group.centerImagePath,
                acNo: group.code || "N/A",
                feeTypeName: "Monthly Fee", 
                fatherName: group.fatherName || "N/A",
                contact: "N/A", 
                
                // Detailed fees
                paperFund: formatMoney(getFee("Paper")),
                fine: formatMoney(getFee("Fine")),
                other: formatMoney(getFee("Other")), 
                transportFee: formatMoney(getFee("Transport")),
                tutionFee: formatMoney(getFee("Tution")),
                annualStationery: formatMoney(getFee("Annual")),
                stationery: formatMoney(getFee("Stationery")),
                balance: formatMoney(getFee("Arrears") || 0), 
                
                totalDue: formatMoney(group.totalAmount),
                totalAfter: formatMoney(group.totalAmount + 200), 
            };
        });

        setVouchers(uniqueVouchers);
        setShowVouchers(true);
      } else {
        setVouchers([]);
        setShowVouchers(true);
      }
    } catch (error) {
      console.error("Error fetching students for vouchers:", error);
      alert("Failed to fetch students.");
    } finally {
      setLoading(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedClassId("");
    setSelectedSectionId("");
    setSelectedPeriodId("");
    setVouchers([]);
    setShowVouchers(false);
  };

  // Format number as PKR for local use inside BulkFeeVoucher
  const formatMoney = (num) => {
    return (num || 0).toLocaleString("en-PK");
  };
  
  const formatMoneyPKR = (num) => {
    return "PKR " + (num || 0).toLocaleString("en-PK");
  };

  // ---------- Styles ----------
  const inputStyle = {
    height: "36px",
    padding: "0 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "13px",
    minWidth: "150px",
    outline: "none",
    color: "#111827",
    backgroundColor: "#ffffff",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#6b7280",
    marginBottom: "4px",
    display: "block",
  };

  const btnPrimary = {
    height: "36px",
    padding: "0 18px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#3b82f6",
    color: "#fff",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  const btnSecondary = {
    height: "36px",
    padding: "0 18px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  };

  return (
    <div style={{ paddingBottom: "20px", fontFamily: "Inter, Arial, sans-serif" }}>
      {/* Header */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          padding: "20px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        className="no-print"
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#111827" }}>
            Bulk Fee Voucher Generator
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#6b7280" }}>
            Generate and print fee vouchers for an entire class/section
          </p>
        </div>
        {showVouchers && vouchers.length > 0 && (
          <button
            onClick={() => window.print()}
            style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: "6px" }}
          >
            🖨 Print All Vouchers
          </button>
        )}
      </div>

      <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Filter Panel */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px 24px",
          }}
          className="no-print"
        >
          <p style={{ margin: "0 0 14px", fontSize: "13px", fontWeight: 600, color: "#374151" }}>
            Select Criteria
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
            
            {/* Center */}
            <div className="d-none">
              <label style={labelStyle}>Center</label>
              <select
                value={selectedCenterId}
                onChange={(e) => setSelectedCenterId(e.target.value)}
                style={inputStyle}
              >
                <option value="">— Select Center —</option>
                {centersData.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Class */}
            <div>
              <label style={labelStyle}>Class</label>
              <select
                value={selectedClassId}
                onChange={(e) => {
                    setSelectedClassId(e.target.value);
                    setSelectedSectionId("");
                }}
                style={inputStyle}
              >
                <option value="">— Select Class —</option>
                {classesData.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Section */}
            <div>
              <label style={labelStyle}>Section</label>
              <select
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                disabled={!selectedClassId}
                style={{
                  ...inputStyle,
                  backgroundColor: !selectedClassId ? "#f9fafb" : "#ffffff",
                  color: !selectedClassId ? "#9ca3af" : "#111827",
                }}
              >
                <option value="">All Sections</option>
                {sectionsData.map((sec) => (
                  <option key={sec.id} value={sec.id}>
                    {sec.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Month (Session Period) */}
            <div>
              <label style={labelStyle}>Session Period (Month)</label>
              <select
                value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
                style={inputStyle}
              >
                <option value="">— Select Month —</option>
                {sessionPeriods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
              <button 
                onClick={handleApply} 
                style={btnPrimary}
                disabled={loading}
              >
                {loading ? "Loading..." : "Apply"}
              </button>
              <button onClick={resetFilters} style={btnSecondary}>
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Vouchers Display Area */}
        {showVouchers && (
          <>
            {vouchers.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "#6b7280",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                }}
                className="no-print"
              >
                <div style={{ fontSize: "2rem", marginBottom: "12px" }}>📭</div>
                No vouchers found for the selected criteria.
              </div>
            ) : (
              <div>
                <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "12px" }} className="no-print">
                  Total {vouchers.length} vouchers generated. (2 per page for printing)
                </p>
                {/* Container for display and print */}
                <div className="vouchers-main-container">
                  {/* For screen: normal side-by-side list */}
                  <div className="vouchers-screen-list no-print">
                    {vouchers.map((voucher) => (
                      <div key={voucher.id} className="voucher-item-wrapper">
                        {String(voucher.centerId) === "2076" ? (
                          <div className="custom-voucher-2076">
                            <VoucherContent data={voucher} />
                            <div className="cut-line">
                              <div className="cut-text">✂️ Cut Here</div>
                            </div>
                            <VoucherContent data={voucher} hideHeader={true} />
                          </div>
                        ) : (
                          <VoucherCard voucher={voucher} formatMoney={formatMoneyPKR} />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* For print: explicitly grouped pages of 2 vouchers each */}
                  <div className="vouchers-print-only print-only">
                    {chunkArray(vouchers, 2).map((pair, pageIndex) => (
                      <div key={pageIndex} className="print-page">
                        {pair.map((voucher) => (
                          <div key={voucher.id} className="print-voucher-wrapper">
                            {String(voucher.centerId) === "2076" ? (
                              <div className="custom-voucher-2076 print-compact">
                                <VoucherContent data={voucher} />
                                <div className="cut-line"> <div className="cut-text">✂️ Cut Here</div> </div>
                                <VoucherContent data={voucher} hideHeader={true} />
                              </div>
                            ) : (
                              <VoucherCard voucher={voucher} formatMoney={formatMoneyPKR} />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        /* Screen Styles */
        .print-only { display: none !important; }
        .vouchers-screen-list {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          justify-content: flex-start;
        }
        .voucher-item-wrapper {
          width: calc(50% - 10px);
          box-sizing: border-box;
        }
        .custom-voucher-2076 {
          background: #fff;
          padding: 15px;
          border: 1px solid #ccc;
          box-sizing: border-box;
        }
        .cut-line {
          border-top: 2px dashed #000;
          margin: 12px 0;
          position: relative;
        }
        .cut-text {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 0 8px;
          font-size: 10px;
        }

        /* Print Styles - Robust Multi-Page Support */
        @media print {
          @page {
            size: A4 portrait;
            margin: 5mm; /* Minimal margins */
          }
          
          /* 1. Hide unwanted UI elements */
          .no-print, header, footer, aside, nav, .sidebar, .navbar, .topbar, [class*="sidebar"], [class*="header"], [class*="topbar"] { 
            display: none !important; 
          }
          
          /* 2. Reset containers to allow multi-page flow and page breaks */
          html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
          }

          #root, #root > div, .App, .main-layout, .content-wrapper, .main-content, .page-content, 
          div[class*="content"], div[class*="main"], div[class*="wrapper"] {
            display: block !important;
            height: auto !important;
            overflow: visible !important;
            position: static !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
            min-height: 0 !important;
            width: 100% !important;
          }

          /* 3. Specifically show only the print container */
          .vouchers-print-only {
            display: block !important;
            visibility: visible !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            position: static !important;
            transform: none !important; /* Removed scale to use full width */
          }

          /* Ensure pages break correctly */
          .print-page {
            width: 100% !important;
            display: block !important;
            page-break-after: always !important;
            break-after: page !important;
            margin-bottom: 0 !important;
            padding: 0 !important;
          }

          .print-voucher-wrapper {
            width: 100% !important;
            margin-bottom: 10px !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            display: block !important;
            position: relative;
            padding: 0 !important;
          }

          /* Compact styling for 2076 custom voucher block */
          .print-compact {
            padding: 5px !important;
            border: 1px solid #333 !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: none !important;
            box-sizing: border-box;
          }
          
          .print-compact * {
            font-size: 10px !important;
            line-height: 1.2 !important;
          }
          .print-compact img[alt="Barcode"] {
            height: 35px !important;
            width: 120px !important;
          }
          .print-compact .cut-line { 
            margin: 10px 0 !important; 
          }

          .voucher-card {
            padding: 15px !important;
            font-size: 13px !important;
            box-shadow: none !important;
            border: 1px solid #333 !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: none !important;
            box-sizing: border-box;
          }
        }
      `}</style>
    </div>
  );
}

// Helper to split array into chunks of size n
const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

// Default Voucher Card Component
const VoucherCard = ({ voucher, formatMoney }) => {
  const cardStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "20px",
    boxSizing: "border-box",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    fontFamily: "Inter, Arial, sans-serif",
    height: "100%", 
  };

  const headerStyle = {
    borderBottom: "2px solid #3b82f6",
    paddingBottom: "8px",
    marginBottom: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    fontSize: "13px",
  };

  const labelStyle = {
    color: "#6b7280",
    fontWeight: 500,
  };

  const valueStyle = {
    color: "#111827",
    fontWeight: 600,
  };

  return (
    <div className="voucher-card" style={cardStyle}>
      <div style={headerStyle}>
        <span style={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>Fee Voucher</span>
        <span style={{ fontSize: "12px", color: "#6b7280" }}>{voucher.voucherNo}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Student Name</span>
        <span style={valueStyle}>{voucher.studentName}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>A/C #</span>
        <span style={valueStyle}>{voucher.rollNo}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Class & Section</span>
        <span style={valueStyle}>{voucher.className} - {voucher.section}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Month</span>
        <span style={valueStyle}>{voucher.month}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Fee Amount</span>
        <span style={{ ...valueStyle, color: "#10b981", fontSize: "15px" }}>{formatMoney(voucher.feeAmount)}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Due Date</span>
        <span style={valueStyle}>{voucher.dueDate}</span>
      </div>
      <div style={{ marginTop: "12px", fontSize: "10px", color: "#9ca3af", textAlign: "center", borderTop: "1px dashed #e5e7eb", paddingTop: "8px" }}>
        This is a computer‑generated voucher – no signature required
      </div>
    </div>
  );
};

export default BulkFeeVoucher;